/**
 * Saisie des notes de devoir — mobile-friendly.
 *
 * Étape 1 : choisir une matière+classe parmi celles enseignées
 * Étape 2 : grille étudiants × (D1, D2, Absent) + bouton Enregistrer.
 */
import { useState, useMemo, useEffect } from 'react';
import {
  PenTool, Loader2, Save, ArrowLeft, BookOpen, GraduationCap, Check, Lock,
  AlertTriangle, XCircle, RotateCcw,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import {
  useMesMatieresNotesQuery, useNotesDevoirMatiereQuery,
  useEnregistrerNotesDevoirMutation,
  useSignalerPasDeDevoirMutation,
  type MatiereNotesItem,
} from '../../services/formateurApi';
import {
  useMesDemandesFenetreQuery, useDemanderFenetreMutation,
} from '../../services/formateurNotesApi';

export default function SaisieNotes() {
  const { data: liste, isLoading, refetch } = useMesMatieresNotesQuery();
  const [sel, setSel] = useState<MatiereNotesItem | null>(null);
  const [signaler, { isLoading: signalPending }] = useSignalerPasDeDevoirMutation();
  // Modal "signaler pas de devoir" — attend un motif obligatoire
  const [modalSignal, setModalSignal] = useState<null | { matiere: MatiereNotesItem; mode: 'activer' | 'reactiver' }>(null);
  const [motif, setMotif] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  // Filtre semestre local (S1 = semestres LMD impairs, S2 = pairs).
  const [filtreSem, setFiltreSem] = useState<'tous' | 'S1' | 'S2'>('tous');
  // Filtre CS (par défaut caché) — sauvegardé dans localStorage
  const [showCS, setShowCS] = useState<boolean>(() => {
    try { return localStorage.getItem('formateur_show_cs') === '1'; } catch { return false; }
  });
  const listeFiltree = useMemo(() => {
    let results = liste?.results || [];
    // Masquer les cours du soir si non demandé (classe se terminant par -CS)
    if (!showCS) {
      results = results.filter((m) => !(m.classe || '').endsWith('-CS'));
    }
    if (filtreSem === 'tous') return results;
    return results.filter((m) => {
      const s = Number(m.semestre) || 0;
      const estS1 = s % 2 === 1;
      return filtreSem === 'S1' ? estS1 : !estS1 && s > 0;
    });
  }, [liste, filtreSem, showCS]);
  // Compter le nombre de matières CS (pour badge)
  const nbCS = (liste?.results || []).filter((m) => (m.classe || '').endsWith('-CS')).length;

  // Fix 2026-09-01 : gérer le warning 409 "notes_deja_saisies"
  // Le backend renvoie ce warning si des notes de devoir existent déjà
  // avec des valeurs (protection anti-écrasement). L'opérateur doit
  // confirmer explicitement avec `force=true`.
  const [confirmForce, setConfirmForce] = useState<null | {
    message: string; nb: number;
  }>(null);

  const doSignaler = async (force = false) => {
    if (!modalSignal) return;
    const actif = modalSignal.mode === 'activer';
    if (actif && motif.trim().length < 5) {
      setFeedback('Un motif d\'au moins 5 caractères est requis.');
      return;
    }
    try {
      await signaler({
        matiere_id: modalSignal.matiere.matiere_id,
        actif, motif: motif.trim(),
        force,
      } as any).unwrap();
      setModalSignal(null); setMotif(''); setFeedback(null);
      setConfirmForce(null);
      refetch();
    } catch (err: any) {
      const data = err?.data;
      if (data?.warning && data?.type === 'notes_deja_saisies' && !force) {
        setConfirmForce({
          message: data.message || 'Notes déjà saisies pour cette matière.',
          nb: data.nb_notes_deja_saisies || 0,
        });
        return;
      }
      setFeedback(data?.error || data?.message || 'Erreur');
    }
  };
  const handleSubmitSignal = () => doSignaler(false);

  if (sel) {
    return <GrilleSaisie sel={sel} onBack={() => setSel(null)} />;
  }

  return (
    <FormateurLayout title="Saisir mes notes">
      <p className="text-xs text-gray-400 mb-3">
        Sélectionnez une matière pour saisir ou modifier les notes de devoirs (D1 et D2).
      </p>
      {/* Toggle CS (cours du soir) — masqué par défaut */}
      {!isLoading && nbCS > 0 && (
        <div className="mb-2 flex items-center justify-end">
          <button
            onClick={() => {
              const next = !showCS;
              setShowCS(next);
              try { localStorage.setItem('formateur_show_cs', next ? '1' : '0'); } catch {}
            }}
            className={`text-[11px] px-2.5 py-1 rounded border transition ${
              showCS
                ? 'bg-purple-900/30 text-purple-300 border-purple-700/60'
                : 'bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700'
            }`}
          >
            {showCS ? '× Masquer CS' : `+ Afficher CS (${nbCS})`}
          </button>
        </div>
      )}
      {/* Filtre semestre : tous / S1 / S2 */}
      {!isLoading && (liste?.results || []).length > 0 && (
        <div className="mb-3 flex items-center gap-1.5 bg-gray-900 border border-gray-800 rounded-lg p-1">
          {(['tous', 'S1', 'S2'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFiltreSem(s)}
              className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded transition-colors ${
                filtreSem === s
                  ? 'bg-amber-600 text-white'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
            >
              {s === 'tous' ? 'Tous' : s}
              <span className="ml-1 text-[10px] opacity-70">
                ({(liste?.results || []).filter((m) => {
                  if (s === 'tous') return true;
                  const sem = Number(m.semestre) || 0;
                  const estS1 = sem % 2 === 1;
                  return s === 'S1' ? estS1 : !estS1 && sem > 0;
                }).length})
              </span>
            </button>
          ))}
        </div>
      )}
      {isLoading ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
        </div>
      ) : listeFiltree.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          {filtreSem === 'tous'
            ? 'Aucune matière assignée pour le moment.'
            : `Aucune matière du semestre ${filtreSem}.`}
        </div>
      ) : (
        <>
        <div className="space-y-1.5">
          {listeFiltree.map((m, i) => {
            const autoReport = m.pas_de_devoir_autorise;
            return (
              <div key={`${m.matiere_id}-${m.classe}-${i}`}
                   className={`w-full bg-gray-900 border rounded-lg p-3 text-left transition ${
                     autoReport ? 'border-indigo-700/60' : 'border-gray-800 hover:border-amber-700/60'
                   }`}>
                <button onClick={() => setSel(m)} className="w-full text-left">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white truncate flex items-center gap-1.5 flex-wrap">
                        {m.nom_matiere}
                        {autoReport && (
                          <span className="inline-block px-1.5 py-0.5 bg-indigo-900/50 text-indigo-300 text-[9px] font-bold rounded border border-indigo-700/60">
                            AUTO-REPORT
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-2">
                        <span className="inline-flex items-center gap-0.5"><BookOpen size={10} /> {m.code_matiere}</span>
                        <span>•</span>
                        <span>S{m.semestre}</span>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-900/30 text-amber-300 border border-amber-700/60 rounded flex-shrink-0 inline-flex items-center gap-0.5">
                      <GraduationCap size={10} /> {m.classe}
                    </span>
                  </div>
                </button>
                <div className="mt-2 flex items-center justify-end">
                  {autoReport ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setModalSignal({ matiere: m, mode: 'reactiver' }); }}
                      className="inline-flex items-center gap-1 text-[10px] text-indigo-300 hover:text-indigo-200 border border-indigo-700/40 rounded px-2 py-1"
                      title="Annuler la déclaration — un devoir sera à saisir"
                    >
                      <RotateCcw size={10} /> Réactiver le devoir
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setModalSignal({ matiere: m, mode: 'activer' }); }}
                      className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-red-300 border border-gray-800 rounded px-2 py-1"
                      title="Déclarer qu'aucun devoir ne sera organisé sur cette matière"
                    >
                      <AlertTriangle size={10} /> Pas de devoir prévu
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal signalement pas de devoir / réactivation */}
        {modalSignal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !signalPending && setModalSignal(null)}>
            <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                <h3 className="text-sm font-bold text-white">
                  {modalSignal.mode === 'activer' ? 'Aucun devoir sur cette matière ?' : 'Réactiver le devoir'}
                </h3>
                <button onClick={() => setModalSignal(null)} className="text-gray-400 hover:text-white"><XCircle size={16} /></button>
              </div>
              <div className="p-4 space-y-3">
                <div className="text-xs text-gray-300">
                  <div className="font-semibold text-white">{modalSignal.matiere.nom_matiere}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{modalSignal.matiere.code_matiere} · {modalSignal.matiere.classe}</div>
                </div>
                {modalSignal.mode === 'activer' ? (
                  <>
                    <p className="text-xs text-gray-400">
                      En déclarant ceci, la note d'examen ordinaire sera automatiquement recopiée
                      comme note de devoir (D1 = D2 = note examen). La scolarité en sera informée.
                    </p>
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-1">Motif (obligatoire)</label>
                      <textarea
                        value={motif}
                        onChange={(e) => { setMotif(e.target.value); setFeedback(null); }}
                        rows={3}
                        placeholder="Ex : Matière pratique sans devoir prévu au programme"
                        className="w-full text-xs bg-gray-800 border border-gray-700 rounded p-2 text-white"
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-gray-400">
                    Vous allez ré-activer la saisie de devoir sur cette matière. L'auto-report examen → devoir sera désactivé.
                  </p>
                )}
                {feedback && <div className="text-[11px] text-red-400">{feedback}</div>}
                {/* Fix 2026-09-01 : confirmation force si notes déjà saisies */}
                {confirmForce && (
                  <div className="bg-red-900/30 border border-red-700 rounded p-2.5 text-[11px] text-red-100 space-y-1.5">
                    <div className="flex items-start gap-1.5">
                      <AlertTriangle size={12} className="text-red-300 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-bold text-red-200">Notes déjà saisies ({confirmForce.nb})</div>
                        <div className="text-red-200/90 mt-0.5">{confirmForce.message}</div>
                      </div>
                    </div>
                    <div className="text-red-300/80 text-[10px]">
                      Note : les notes existantes ne seront pas écrasées par l'auto-report
                      (protection anti-écrasement). Le flag sera activé pour les futures matières
                      sans notes.
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button onClick={() => setConfirmForce(null)}
                        className="px-2 py-1 text-[10px] text-red-200 hover:bg-red-900/40 rounded">
                        Annuler
                      </button>
                      <button onClick={() => doSignaler(true)}
                        disabled={signalPending}
                        className="px-2 py-1 text-[10px] font-semibold bg-red-600 hover:bg-red-500 text-white rounded">
                        Activer quand même
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={() => setModalSignal(null)} disabled={signalPending}
                          className="px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-800 rounded">
                    Annuler
                  </button>
                  <button onClick={handleSubmitSignal} disabled={signalPending}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded ${
                            modalSignal.mode === 'activer'
                              ? 'bg-red-700 hover:bg-red-600 text-white'
                              : 'bg-indigo-700 hover:bg-indigo-600 text-white'
                          } disabled:opacity-60`}>
                    {signalPending ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                    {modalSignal.mode === 'activer' ? 'Confirmer' : 'Réactiver'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </>
      )}
    </FormateurLayout>
  );
}

function GrilleSaisie({ sel, onBack }: { sel: MatiereNotesItem; onBack: () => void }) {
  const { data, isLoading, refetch } = useNotesDevoirMatiereQuery({
    matiere_id: sel.matiere_id, classe: sel.classe,
  });
  const [modifications, setModifications] = useState<Record<number, {
    note1?: number | null; note2?: number | null;
    absent1?: boolean; absent2?: boolean;
  }>>({});
  const [enregistrer, { isLoading: saving }] = useEnregistrerNotesDevoirMutation();
  const [message, setMessage] = useState('');

  // Reset quand on charge une nouvelle matière
  useEffect(() => { setModifications({}); setMessage(''); }, [sel.matiere_id, sel.classe]);

  const nbModif = Object.keys(modifications).length;

  const update = (etu_id: number, patch: Partial<typeof modifications[number]>) => {
    setModifications(prev => ({ ...prev, [etu_id]: { ...prev[etu_id], ...patch } }));
  };

  const handleSave = async () => {
    if (nbModif === 0) return;
    setMessage('');
    const notesArr = Object.entries(modifications).map(([id, m]) => ({
      etudiant_id: Number(id),
      note1: m.note1,
      note2: m.note2,
      absent1: m.absent1,
      absent2: m.absent2,
    }));
    try {
      const res = await enregistrer({
        matiere_id: sel.matiere_id, classe: sel.classe, notes: notesArr,
      }).unwrap();
      setMessage(`${res.nb_enregistrees} note(s) enregistrée(s)`);
      setModifications({});
      refetch();
    } catch (err: any) {
      setMessage(err?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  return (
    <FormateurLayout title={sel.nom_matiere}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 mb-3 flex items-center gap-2">
        <button onClick={onBack} className="text-[11px] text-gray-400 hover:text-white inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Retour
        </button>
        <div className="flex-1 text-center">
          <div className="text-xs font-bold text-white">{sel.code_matiere} • {sel.classe}</div>
          <div className="text-[10px] text-gray-500">Sem. {data?.semestre_str || `S${sel.semestre}`} • {data?.annee_academique || ''}</div>
        </div>
        <span className="text-[10px] text-gray-400">{data?.total || 0} étud.</span>
      </div>

      {isLoading ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
        </div>
      ) : (
        <>
          {/* Bandeau : saisie autorisée ou verrouillée */}
          {data && (() => {
            const autorise = data.saisie_autorisee;
            const f = data.fenetre;
            if (autorise) {
              return (
                <div className="bg-emerald-900/20 border border-emerald-700/60 text-emerald-200 rounded-lg p-2.5 mb-3 text-xs flex items-start gap-2">
                  <Check size={12} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Saisie ouverte</strong> — {f.libelle || 'Fenêtre active'}
                    {f.date_fin && (
                      <> · clôture le <strong>{new Date(f.date_fin).toLocaleDateString('fr-FR')}</strong></>
                    )}
                  </div>
                </div>
              );
            }
            // Semestre "local" (1 ou 2) = parité du semestre LMD (1..6)
            const semLocal = sel?.semestre && sel.semestre % 2 === 0 ? 2 : 1;
            return (
              <BandeauFenetreFermee
                message={f.message}
                semestreStr={data.semestre_str}
                annee={data.annee_academique}
                classe={sel?.classe || ''}
                matiereLibelle={sel?.nom_matiere || ''}
                matiereCode={sel?.code_matiere || ''}
                semestreNum={semLocal}
              />
            );
          })()}

          <div className="space-y-1 mb-20">
            {(data?.items || []).map(it => {
              const m = modifications[it.etudiant_id] || {};
              const n1 = m.note1 !== undefined ? m.note1 : it.note1;
              const n2 = m.note2 !== undefined ? m.note2 : it.note2;
              const a1 = m.absent1 !== undefined ? m.absent1 : it.absent1;
              const a2 = m.absent2 !== undefined ? m.absent2 : it.absent2;
              const readOnly = it.verrouille || !data?.saisie_autorisee;
              return (
                <div key={it.etudiant_id} className={`bg-gray-900 border rounded-lg p-2 ${
                  modifications[it.etudiant_id]
                    ? 'border-amber-700/60'
                    : 'border-gray-800'
                } ${readOnly ? 'opacity-60' : ''}`}>
                  <div className="text-xs font-semibold text-white truncate mb-1">
                    <span className="uppercase">{it.nom}</span> {it.prenoms}
                    {it.verrouille && (
                      <span className="ml-1 text-[9px] px-1 py-0.5 bg-red-900/40 text-red-300 rounded inline-flex items-center gap-0.5">
                        <Lock size={8} /> Note verrouillée
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <NoteCell label="D1" value={n1} absent={a1} disabled={readOnly}
                      onChange={(v) => update(it.etudiant_id, { note1: v })}
                      onAbsent={(b) => update(it.etudiant_id, { absent1: b })} />
                    <NoteCell label="D2" value={n2} absent={a2} disabled={readOnly}
                      onChange={(v) => update(it.etudiant_id, { note2: v })}
                      onAbsent={(b) => update(it.etudiant_id, { absent2: b })} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer fixe — uniquement si saisie autorisée */}
          {data?.saisie_autorisee && (
            <div className="fixed bottom-0 left-0 right-0 z-30 bg-gray-900 border-t border-gray-800">
              <div className="max-w-3xl mx-auto px-3 py-2.5 flex items-center gap-3">
                <div className="text-xs flex-1">
                  {message ? (
                    <span className="text-emerald-300 inline-flex items-center gap-1">
                      <Check size={11} /> {message}
                    </span>
                  ) : nbModif > 0 ? (
                    <span className="text-amber-300">{nbModif} modification(s) en attente</span>
                  ) : (
                    <span className="text-gray-500">Aucune modification</span>
                  )}
                </div>
                <button onClick={handleSave} disabled={nbModif === 0 || saving}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-semibold rounded inline-flex items-center gap-1.5">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </FormateurLayout>
  );
}

function NoteCell({ label, value, absent, disabled, onChange, onAbsent }: {
  label: string;
  value: number | null | undefined;
  absent: boolean;
  disabled: boolean;
  onChange: (v: number | null) => void;
  onAbsent: (b: boolean) => void;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase font-semibold text-gray-400 mb-0.5 flex items-center justify-between">
        <span>{label}</span>
        <label className="inline-flex items-center gap-1 text-[9px] text-gray-500 cursor-pointer">
          <input type="checkbox" checked={absent} disabled={disabled}
            onChange={(e) => onAbsent(e.target.checked)}
            className="w-3 h-3 accent-red-500" />
          ABS
        </label>
      </div>
      <input type="number" min={0} max={20} step={0.25}
        value={value ?? ''} disabled={disabled || absent}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? null : Math.max(0, Math.min(20, Number(v))));
        }}
        placeholder={absent ? '0 (abs)' : '/20'}
        className="w-full px-2 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded text-center text-white font-mono
          disabled:opacity-60 focus:border-amber-500 focus:outline-none" />
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Bandeau affiché quand la fenêtre de saisie est fermée pour cette matière.
// Permet au formateur de demander l'ouverture d'une fenêtre exceptionnelle.
// (fix 2026-09-01)
// ═════════════════════════════════════════════════════════════════════════
function BandeauFenetreFermee({
  message, semestreStr, annee, classe, matiereLibelle, matiereCode, semestreNum,
}: {
  message: string; semestreStr: string; annee: string;
  classe: string; matiereLibelle: string; matiereCode: string; semestreNum: number;
}) {
  const [showModal, setShowModal] = useState(false);
  const { data: mesDemandes } = useMesDemandesFenetreQuery();

  // Y a-t-il déjà une demande en attente pour cette classe + matière ?
  const demandeExistante = (mesDemandes?.results || []).find(d =>
    d.statut === 'en_attente'
    && d.classe.toUpperCase() === classe.toUpperCase()
    && d.matiere_libelle.toLowerCase() === matiereLibelle.toLowerCase()
  );

  return (
    <>
      <div className="bg-amber-900/20 border border-amber-700/60 text-amber-200 rounded-lg p-3 mb-3 text-xs">
        <div className="flex items-start gap-2 mb-2">
          <Lock size={14} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold mb-0.5">Saisie verrouillée — consultation uniquement</div>
            <div className="opacity-90">{message}</div>
            <div className="mt-1 text-amber-300/80">
              La fenêtre de saisie est fermée pour le semestre {semestreStr} ({annee}).
            </div>
          </div>
        </div>
        {demandeExistante ? (
          <div className="mt-2 pl-6 text-[11px] text-amber-300/90 bg-amber-900/30 rounded p-2 border border-amber-800/40">
            <Check size={11} className="inline mr-1" />
            Demande envoyée le {new Date(demandeExistante.date_demande).toLocaleDateString('fr-FR')} — en attente de validation.
          </div>
        ) : (
          <div className="mt-2 pl-6">
            <button
              onClick={() => setShowModal(true)}
              className="px-3 py-1.5 text-[11px] font-medium bg-amber-500 hover:bg-amber-600 text-amber-950 rounded inline-flex items-center gap-1.5"
            >
              <PenTool size={11} /> Demander l'ouverture d'une fenêtre
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <ModalDemanderFenetre
          classe={classe}
          matiereLibelle={matiereLibelle}
          matiereCode={matiereCode}
          semestreNum={semestreNum}
          annee={annee}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}


function ModalDemanderFenetre({
  classe, matiereLibelle, matiereCode, semestreNum, annee, onClose,
}: {
  classe: string; matiereLibelle: string; matiereCode: string;
  semestreNum: number; annee: string; onClose: () => void;
}) {
  const [motif, setMotif] = useState('');
  const [duree, setDuree] = useState(7);
  const [envoye, setEnvoye] = useState(false);
  const [demander, { isLoading }] = useDemanderFenetreMutation();

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    if (motif.trim().length < 10) {
      alert('Merci de fournir un motif d\'au moins 10 caractères.');
      return;
    }
    try {
      await demander({
        classe, matiere_libelle: matiereLibelle, matiere_code: matiereCode,
        semestre: semestreNum, type_notes: 'devoirs',
        motif: motif.trim(), duree_jours: duree,
        annee_libelle: annee,
      }).unwrap();
      setEnvoye(true);
      setTimeout(onClose, 2000);
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur lors de l\'envoi.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl max-w-md w-full">
        <header className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
          <h3 className="font-bold text-white text-sm">Demander une fenêtre de saisie</h3>
          <button onClick={onClose} className="text-gray-400"><XCircle size={18} /></button>
        </header>

        {envoye ? (
          <div className="p-6 text-center">
            <Check size={40} className="mx-auto text-green-500 mb-3" />
            <div className="text-white font-semibold mb-1">Demande envoyée</div>
            <div className="text-xs text-gray-400">
              La scolarité examinera votre demande. Vous serez notifié dès qu'elle sera traitée.
            </div>
          </div>
        ) : (
          <form onSubmit={soumettre} className="p-5 space-y-3">
            <div className="bg-gray-800/50 rounded-lg p-3 text-xs space-y-1">
              <div><span className="text-gray-500">Classe :</span> <span className="font-mono text-white">{classe}</span></div>
              <div><span className="text-gray-500">Matière :</span> <span className="text-white">{matiereLibelle}</span></div>
              <div><span className="text-gray-500">Semestre local :</span> <span className="text-white">S{semestreNum}</span></div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Motif <span className="text-red-500">*</span> <span className="text-gray-500">(min 10 caractères)</span>
              </label>
              <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={4}
                placeholder="Ex : j'étais en mission externe pendant la période de saisie officielle et n'ai pas pu…"
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
              <div className="text-[10px] text-gray-500 mt-1">{motif.length}/2000 caractères</div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Durée souhaitée (jours)</label>
              <input type="number" min={1} max={30} value={duree}
                onChange={(e) => setDuree(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-white" />
            </div>

            <div className="bg-blue-900/20 border border-blue-800/50 rounded p-2 text-[11px] text-blue-200">
              <AlertTriangle size={11} className="inline mr-1" />
              La scolarité examinera votre demande. En cas d'approbation, une fenêtre
              exceptionnelle sera ouverte immédiatement et vous serez notifié.
            </div>

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 px-3 py-2 text-sm bg-gray-800 text-gray-300 rounded hover:bg-gray-700">
                Annuler
              </button>
              <button type="submit" disabled={isLoading || motif.trim().length < 10}
                className="flex-1 px-3 py-2 text-sm bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-amber-950 font-semibold rounded inline-flex items-center justify-center gap-1.5">
                {isLoading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Envoyer
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
